import i18n from '@/i18n';

/**
 * A `Record<K, string>` whose values are resolved through i18next at access
 * time, so a shared enum-label map reads in the active language without every
 * `MAP[value]` call site having to call `t()` itself.
 *
 * `keys` maps each enum value to its translation key in `ns`. The returned proxy
 * behaves like the old plain object for reads and enumeration (`MAP[k]`,
 * `Object.entries(MAP)`, `k in MAP`), but each value is translated on read via
 * i18next — so it follows a language change (react-i18next re-renders on
 * `languageChanged`) and works the same in a `.ts` helper as in a component.
 */
export function i18nMap<K extends string>(ns: string, keys: Record<K, string>): Record<K, string> {
  return new Proxy({} as Record<K, string>, {
    get(_target, prop) {
      if (typeof prop !== 'string' || !(prop in keys)) return undefined;
      return i18n.t(`${ns}:${keys[prop as K]}`);
    },
    has(_target, prop) {
      return typeof prop === 'string' && prop in keys;
    },
    ownKeys() {
      return Reflect.ownKeys(keys);
    },
    getOwnPropertyDescriptor() {
      return { enumerable: true, configurable: true };
    },
  });
}

/**
 * Like {@link i18nMap} for a map of objects — `{ label, tone }`, `{ label, hint,
 * color }` — where only some fields are text. `fields` (default `label`, `hint`)
 * hold translation keys and are resolved on read; every other field (a tone, a
 * colour) passes through unchanged. Reading returns a fresh object each time.
 */
/**
 * Like {@link i18nMetaMap} for an array of objects — `[{ label, hint? }, …]`.
 * The `fields` (default `label`, `hint`) hold translation keys, resolved on read;
 * indexing (`arr[i].label`), iteration and `arr.map(x => x.label)` all yield
 * translated items, so a module-level options array reads in the active language
 * with no change at its call sites.
 */
export function i18nArray<T extends object>(
  ns: string,
  items: T[],
  fields: string[] = ['label', 'hint'],
): T[] {
  return new Proxy(items, {
    get(target, prop, receiver) {
      const v = Reflect.get(target, prop, receiver);
      if (typeof prop === 'string' && /^\d+$/.test(prop) && v && typeof v === 'object') {
        const out = { ...(v as Record<string, unknown>) };
        for (const f of fields) {
          if (typeof out[f] === 'string') out[f] = i18n.t(`${ns}:${out[f] as string}`);
        }
        return out;
      }
      return v;
    },
  });
}

export function i18nMetaMap<K extends string, V extends object>(
  ns: string,
  meta: Record<K, V>,
  fields: string[] = ['label', 'hint'],
): Record<K, V> {
  return new Proxy({} as Record<K, V>, {
    get(_target, prop) {
      if (typeof prop !== 'string' || !(prop in meta)) return undefined;
      const out = { ...meta[prop as K] } as Record<string, unknown>;
      for (const f of fields) {
        if (typeof out[f] === 'string') out[f] = i18n.t(`${ns}:${out[f] as string}`);
      }
      return out;
    },
    has(_target, prop) {
      return typeof prop === 'string' && prop in meta;
    },
    ownKeys() {
      return Reflect.ownKeys(meta);
    },
    getOwnPropertyDescriptor() {
      return { enumerable: true, configurable: true };
    },
  });
}
